import { Plus, UsersRound } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { DataTable } from '../../components/common/DataTable';
import { EmptyState } from '../../components/common/EmptyState';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { StatusBadge } from '../../components/common/StatusBadge';
import { createEmployee, getEmployees } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { useToast } from '../../contexts/ToastContext';

const EMPTY_VALUES = { fullName: '', jobTitle: '', email: '', phone: '' };

export function EmployeesPage() {
  const { user } = useAuth();
  const { activeTenant, activeTenantId, role } = useTenant();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(EMPTY_VALUES);

  const tableEmployees = useMemo(() => {
    const currentEmail = String(user?.email || '').trim().toLowerCase();
    const currentUserIndex = employees.findIndex((employee) => String(employee.email || '').trim().toLowerCase() === currentEmail && currentEmail);

    if (currentUserIndex >= 0) {
      return employees.map((employee, index) => index === currentUserIndex ? { ...employee, isCurrentUser: true } : employee);
    }

    return [
      {
        id: 'current-user',
        fullName: user?.displayName || user?.email || 'Current user',
        jobTitle: role?.name || 'Account owner',
        email: user?.email || '',
        phone: '',
        status: 'active',
        isCurrentUser: true,
      },
      ...employees,
    ];
  }, [employees, role?.name, user?.displayName, user?.email]);

  const loadEmployees = useCallback(async () => {
    if (!activeTenantId) {
      setEmployees([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setEmployees(await getEmployees(activeTenantId));
    } catch (error) {
      console.error('Unable to load employees', error);
      notify('Unable to load employees. Try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTenantId, notify]);

  useEffect(() => {
    loadEmployees().catch((error) => console.error(error));
  }, [loadEmployees]);

  function openModal() {
    setValues(EMPTY_VALUES);
    setOpen(true);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!activeTenantId || saving) return;

    setSaving(true);
    try {
      await createEmployee(activeTenantId, user?.uid, values);
      notify('Employee added successfully.');
      setOpen(false);
      setValues(EMPTY_VALUES);
      await loadEmployees();
    } catch (error) {
      console.error('Unable to add employee', error);
      notify('Unable to add employee. Try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="employees-page">
      <PageHeader
        title="Employees"
        description="See the people who help your academy, school or institution run well."
        action={<Button icon={Plus} onClick={openModal}>Add Employee</Button>}
      />
      {loading ? <LoadingState label="Loading employees..." /> : (
        <DataTable
          rows={tableEmployees}
          columns={[
            { key: 'fullName', label: 'Name' },
            { key: 'jobTitle', label: 'Job title', render: (item) => item.jobTitle || 'Not specified' },
            { key: 'email', label: 'Email', render: (item) => item.email || '—' },
            { key: 'phone', label: 'Phone', render: (item) => item.phone || '—' },
            { key: 'status', label: 'Status', render: (item) => <StatusBadge status={item.status} /> },
            { key: 'presence', label: 'Presence', render: (item) => item.isCurrentUser ? <span className="employees-current-badge">Logged in</span> : '—' },
            { key: 'action', label: 'Action', render: (item) => <button type="button" className="employees-view-btn" onClick={() => navigate(`/dashboard/hr/employees/${item.id}`, { state: { employee: item } })}>View</button> },
          ]}
          empty={<EmptyState title="No employees yet." message="Add your first employee to start building the people directory." icon={UsersRound} />}
        />
      )}

      <Modal
        className="employee-modal"
        open={open}
        onClose={() => setOpen(false)}
        title={activeTenant?.type === 'private_instructor' ? 'HR & people operations' : 'Add employee'}
        description={activeTenant?.type === 'private_instructor' ? 'Build your team directory.' : "Add a person to your organisation's employee directory."}
      >
        {activeTenant?.type === 'private_instructor' ? (
          <div className="modal-form account-type-notice">
            <p>This feature is only available for virtual academies, training centers, colleges, universities and schools.</p>
            <p>Your current account type is <strong>private instructor</strong>.</p>
            <div className="form-actions"><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button to="/dashboard/settings" onClick={() => setOpen(false)}>Change your account type to proceed</Button></div>
          </div>
        ) : (
          <form className="modal-form" onSubmit={handleSubmit}>
            <FormField label="Full name" id="employee-name"><input id="employee-name" required value={values.fullName} onChange={(event) => setValues({ ...values, fullName: event.target.value })} /></FormField>
            <FormField label="Job title" id="employee-job-title"><input id="employee-job-title" placeholder="e.g. Admissions officer" value={values.jobTitle} onChange={(event) => setValues({ ...values, jobTitle: event.target.value })} /></FormField>
            <div className="form-grid">
              <FormField label="Email" id="employee-email"><input id="employee-email" type="email" value={values.email} onChange={(event) => setValues({ ...values, email: event.target.value })} /></FormField>
              <FormField label="Phone" id="employee-phone"><input id="employee-phone" type="tel" value={values.phone} onChange={(event) => setValues({ ...values, phone: event.target.value })} /></FormField>
            </div>
            <div className="form-actions"><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" loading={saving}>Add Employee</Button></div>
          </form>
        )}
      </Modal>

    </div>
  );
}
